import { api } from './api';
import type {
  Pipeline,
  PipelineCreateInput,
  PipelineUpdateInput,
  PipelineRun,
  PipelineStepLog,
  PipelineGraph,
  BreakpointDef,
  PipelineRunPauseState,
  PipelineRunStatus,
  PipelineStepStatus,
} from '@elmes/shared';
import { getRealtimeSocket } from './realtimeBus.js';
import { useAuthStore } from '../stores/authStore.js';

// ── F12 Sprint A — Run Stream types ──

export interface RunStreamStep {
  stepId: string;
  nodeType: string;
  nodeLabel: string;
  status: PipelineStepStatus;
  startedAt?: string;
  finishedAt?: string;
  output?: unknown;
  error?: string;
  durationMs?: number;
  tokensUsed?: number;
  toolCalls?: Array<{ tool: string; args: unknown; result: unknown }>;
  reasoning?: string;
}

export type SimulationMode = 'mock' | 'replay' | 'stub' | null;

export interface SimulationContextInput {
  mode: 'mock' | 'replay' | 'stub';
  mockProviderConfig?: { modelOverride?: string; profileIdOverride?: number };
  replayRunId?: number;
  fixtureId?: number;
}

export interface RunStreamSnapshot {
  runId: number;
  pipelineId: number;
  status: PipelineRunStatus;
  startedAt?: string;
  finishedAt?: string;
  currentStepId?: string;
  totalTokens: number;
  totalDurationMs?: number;
  error?: string;
  steps: RunStreamStep[];
  pauseInfo?: { reason: string; pausedAt: string; resumableBy: string };
  /** F12 Sprint C — null = production run */
  simulationMode?: SimulationMode;
}

export interface PipelineRunEvent {
  type: string;
  payload: Record<string, unknown>;
}

interface PaginatedResponse<T> {
  success: boolean;
  items: T[];
  total: number;
}

export const pipelineService = {
  // ── Pipeline CRUD ──

  async list(params?: { status?: string; isTemplate?: boolean; search?: string; page?: number; perPage?: number }) {
    const { data } = await api.get<PaginatedResponse<Pipeline>>('/pipelines', { params });
    return { items: data.items, total: data.total };
  },

  async get(id: number) {
    const { data } = await api.get<{ success: boolean; pipeline: Pipeline }>(`/pipelines/${id}`);
    return data.pipeline;
  },

  async create(input: PipelineCreateInput) {
    const { data } = await api.post<{ success: boolean; pipeline: Pipeline }>('/pipelines', input);
    return data.pipeline;
  },

  async update(id: number, input: PipelineUpdateInput & { expectedVersion?: number }) {
    const { data } = await api.put<{ success: boolean; pipeline: Pipeline }>(`/pipelines/${id}`, input);
    return data.pipeline;
  },

  async remove(id: number) {
    await api.delete(`/pipelines/${id}`);
  },

  // ── Runs ──

  async startRun(pipelineId: number, opts?: {
    triggerType?: string;
    triggerData?: Record<string, unknown>;
    debugMode?: boolean;
    breakpoints?: string[];
    breakpointDefs?: BreakpointDef[];
    watchedVariables?: string[];
    /** F12 Sprint C — simulation mode */
    simulation?: SimulationContextInput;
  }) {
    const { data } = await api.post<{ success: boolean; run: PipelineRun }>(`/pipelines/${pipelineId}/runs`, opts);
    return data.run;
  },

  async listRuns(pipelineId: number, params?: { page?: number; perPage?: number; status?: string }) {
    const { data } = await api.get<{ success: boolean; items: PipelineRun[]; total: number }>(`/pipelines/${pipelineId}/runs`, { params });
    return { items: data.items, total: data.total };
  },

  async getRun(pipelineId: number, runId: number) {
    const { data } = await api.get<{ success: boolean; run: PipelineRun }>(`/pipelines/${pipelineId}/runs/${runId}`);
    return data.run;
  },

  async resumeRun(pipelineId: number, runId: number) {
    const { data } = await api.post<{ success: boolean; run: PipelineRun }>(`/pipelines/${pipelineId}/runs/${runId}/resume`);
    return data.run;
  },

  async pauseRun(pipelineId: number, runId: number) {
    const { data } = await api.post<{ success: boolean; run: PipelineRun }>(`/pipelines/${pipelineId}/runs/${runId}/pause`);
    return data.run;
  },

  async getRunState(pipelineId: number, runId: number) {
    const { data } = await api.get<{ success: boolean } & PipelineRunPauseState>(`/pipelines/${pipelineId}/runs/${runId}/state`);
    return data;
  },

  async cancelRun(pipelineId: number, runId: number) {
    const { data } = await api.post<{ success: boolean; run: PipelineRun }>(`/pipelines/${pipelineId}/runs/${runId}/cancel`);
    return data.run;
  },

  async approveRun(pipelineId: number, runId: number) {
    const { data } = await api.post<{ success: boolean; approved: boolean }>(`/pipelines/${pipelineId}/runs/${runId}/approve`);
    return data;
  },

  async rejectRun(pipelineId: number, runId: number) {
    const { data } = await api.post<{ success: boolean; rejected: boolean }>(`/pipelines/${pipelineId}/runs/${runId}/reject`);
    return data;
  },

  async getStepLogs(pipelineId: number, runId: number) {
    const { data } = await api.get<{ success: boolean; logs: PipelineStepLog[] }>(`/pipelines/${pipelineId}/runs/${runId}/steps`);
    return data.logs;
  },

  // ── AI Generation (Faz 3) ──

  async generateFromPrompt(prompt: string, profileId?: number) {
    const { data } = await api.post<{ success: boolean; graph: PipelineGraph }>('/pipelines/generate', { prompt, profileId });
    return data.graph;
  },

  // ── AI Chat (context-aware) ──

  async aiChat(message: string, currentGraph: PipelineGraph, history?: Array<{ role: string; content: string }>, profileId?: number) {
    const { data } = await api.post<{ success: boolean; graph?: PipelineGraph; message?: string }>('/pipelines/ai-chat', {
      message,
      currentGraph,
      history,
      profileId,
    });
    return data;
  },

  // ── Import/Export ──

  async exportPipeline(id: number) {
    const { data } = await api.get<{ success: boolean; data: Record<string, unknown> }>(`/pipelines/${id}/export`);
    return data.data;
  },

  async importPipeline(importData: Record<string, unknown>) {
    const { data } = await api.post<{ success: boolean; pipeline: Pipeline }>('/pipelines/import', importData);
    return data.pipeline;
  },

  // ── Version History ──

  async listVersions(id: number) {
    const { data } = await api.get<{ success: boolean; versions: Array<{ id: number; version: number; createdAt: string }> }>(`/pipelines/${id}/versions`);
    return data.versions;
  },

  async rollbackToVersion(id: number, version: number) {
    const { data } = await api.post<{ success: boolean; pipeline: Pipeline }>(`/pipelines/${id}/versions/${version}/rollback`);
    return data.pipeline;
  },

  // ── Cost Estimation ──

  async estimateCost(pipelineId: number): Promise<{ totalCostUsd: number; nodes: Array<{ nodeId: string; type: string; costUsd: number }> }> {
    const { data } = await api.post<{ success: boolean; totalCostUsd: number; nodes: Array<{ nodeId: string; type: string; costUsd: number }> }>(`/pipelines/${pipelineId}/estimate-cost`);
    return { totalCostUsd: data.totalCostUsd, nodes: data.nodes };
  },

  // ── F12 Sprint A — Live Execution Snapshot (#308) ──

  async getRunSnapshot(pipelineId: number, runId: number): Promise<RunStreamSnapshot> {
    const { data } = await api.get<{ success: boolean; snapshot: RunStreamSnapshot }>(
      `/pipelines/${pipelineId}/runs/${runId}/snapshot`,
    );
    return data.snapshot;
  },
};

/**
 * Subscribe to live pipeline run events via Socket.IO.
 * Sends pipeline:run:subscribe on open, unsubscribes on cleanup.
 * Returns an unsubscribe function.
 */
export function subscribeToRunStream(
  runId: number,
  onEvent: (e: PipelineRunEvent) => void,
): () => void {
  const token = useAuthStore.getState().accessToken ?? '';
  const socket = getRealtimeSocket(token);

  socket.emit('pipeline:run:subscribe', { runId });

  // Keep this run's events only: one shared socket may watch two runs, and an old server (rolling deploy) may still join late.
  const handler = (e: PipelineRunEvent) => {
    if (e?.payload?.runId === runId) onEvent(e);
  };
  socket.on('pipeline:run:event', handler);

  return () => {
    socket.emit('pipeline:run:unsubscribe', { runId });
    socket.off('pipeline:run:event', handler);
  };
}
